/**
 * Contract advance payment: billed as a Draft Advance Sales Invoice, then recovered through IPC deductions.
 */
import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

type ApiError = { response?: { data?: { message?: string } }; message?: string };
const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || (e as ApiError)?.message || fallback;

export const EpcAdvancePaymentCard: React.FC<{
  contractId: string;
  currency?: string;
  defaultDepartmentId?: string | null;
  canRequest: boolean;
}> = ({ contractId, currency, defaultDepartmentId, canRequest }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ amount: number; departmentId: string; notes?: string }>();

  const posQ = useQuery({
    queryKey: ['epc-contract-advance', contractId],
    queryFn: () => epcContractAdminService.getAdvancePosition(contractId),
    staleTime: 15_000,
  });

  const requestM = useMutation({
    mutationFn: (v: { amount: number; departmentId: string; notes?: string }) =>
      epcContractAdminService.requestAdvancePayment(contractId, v),
    onSuccess: (data) => {
      message.success(`Draft Advance Invoice ${data.invoiceNumber} created`);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['epc-contract-advance', contractId] });
      qc.invalidateQueries({ queryKey: ['epc-pa-draft', contractId] });
      navigate(`/growth/invoices/${data.salesInvoiceId}`);
    },
    onError: (e) => message.error(errMsg(e, 'Advance request failed')),
  });

  const pos = posQ.data;
  if (posQ.isError) return <Alert type="error" showIcon message={errMsg(posQ.error, 'Failed to load advance')} />;
  if (!pos || pos.advancePct <= 0) return null;

  const fmt = (v: unknown) => formatMoney(v, { currency: currency || 'AED' });

  return (
    <Card
      size="small"
      title={`Advance payment (${pos.advancePct}% of contract)`}
      extra={
        canRequest && pos.billable ? (
          <Button
            type="primary"
            size="small"
            disabled={pos.available <= 0}
            onClick={() => {
              form.setFieldsValue({ amount: pos.available, departmentId: defaultDepartmentId || undefined });
              setOpen(true);
            }}
          >
            Request advance
          </Button>
        ) : null
      }
    >
      <Space size="large" wrap>
        <Statistic title="Entitlement" value={pos.entitlement} precision={2} prefix={currency} />
        <Statistic title="Billed" value={pos.billed} precision={2} prefix={currency} />
        {pos.openingReceived > 0 ? (
          <Statistic title="Received before system" value={pos.openingReceived} precision={2} prefix={currency} />
        ) : null}
        <Statistic title="Not yet billed" value={pos.available} precision={2} prefix={currency} />
      </Space>
      <Text type="secondary" style={{ display: 'block', margin: '8px 0' }}>
        Each request raises a Draft Advance Invoice (ADV){pos.vatPct > 0 ? ` with ${pos.vatPct}% VAT` : ' without VAT'}.
        Finance sets the advance accounts and posts it. IPC advance recovery is limited to the advance billed.
        {!pos.billable ? ' Requests open once the contract is Approved or Active.' : ''}
      </Text>
      {pos.requests.length ? (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={pos.requests}
          columns={[
            { title: 'Advance', dataIndex: 'advance_number', width: 140 },
            { title: 'Amount (excl. VAT)', dataIndex: 'amount', width: 160, render: (v) => fmt(v) },
            { title: 'VAT', dataIndex: 'vat_amount', width: 120, render: (v) => fmt(v) },
            {
              title: 'Invoice',
              dataIndex: 'sales_invoice_id',
              render: (v, r) =>
                v ? (
                  <Space size="small">
                    <Link to={`/growth/invoices/${v}`}>{r.invoice_number || String(v).slice(0, 8)}</Link>
                    {r.invoice_status ? (
                      <Tag color={r.invoice_status === 'Cancelled' ? 'red' : undefined}>{r.invoice_status}</Tag>
                    ) : null}
                  </Space>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      ) : null}
      <Modal
        title="Request advance payment"
        open={open}
        onCancel={() => setOpen(false)}
        okText="Create Draft Advance Invoice"
        confirmLoading={requestM.isPending}
        destroyOnClose
        onOk={async () => {
          try {
            requestM.mutate(await form.validateFields());
          } catch {
            /* validation */
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label={`Amount excl. VAT (max ${fmt(pos.available)})`}
            rules={[{ required: true, message: 'Amount is required' }]}
          >
            <InputNumber min={0.01} max={pos.available} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="departmentId" label="Department" rules={[{ required: true, message: 'Department is required' }]}>
            <DepartmentSelect placeholder="Select department" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
